import { Suspense, useEffect, useRef } from 'react'
import { LogOut, X } from 'lucide-react'
import LoadingPane from './LoadingPane'
import ChunkErrorBoundary from './ChunkErrorBoundary'
import { useSubscription } from '../hooks/useSubscription'
import { trackEvent } from '../lib/analytics'
import { trackSegmentEvent } from '../lib/segment'
import { logout } from '../lib/logout'
import { lazyWithRetry } from '../lib/lazyWithRetry'
import './SubscriptionGate.css'

// The full plan-selection pricing page — plan catalog, billing toggle, plan cards, checkout — loads
// as a deferred chunk: the gate renders only when someone actually hits the paywall, so its bytes
// don't belong in the eager entry bundle every load parses (QA bug bug-muvore15-bum3). SettingsModal
// mounts the same picker from its own deferred chunk, so Rollup shares the chunk between the two.
const PlanSelection = lazyWithRetry(() =>
  import('./PlanSelection').then((module) => ({ default: module.PlanSelection })))

// Analytics event emitted once per gate mount. The admin "Pay Wall Activity" section counts distinct
// users carrying this event as the people the paywall confronted (see
// netlify/functions/lib/paywall-activity.ts, PAYWALL_SHOWN_EVENT — keep the two in sync).
const PAYWALL_SHOWN_EVENT = 'plan-gate-shown'

// The gate appears in two distinct situations, and the paywall event carries which one under
// `reason` so the "Pay Wall Activity" funnel can tell them apart:
//   - 'out-of-credits' — an existing subscriber ran out of credits and hit the paywall. This is the
//     conversion funnel the admin section measures.
//   - 'no-subscription' — a brand-new / unsubscribed user shown the plan picker at signup. They have
//     not "expended their credits", so the funnel must NOT count them.
// Mirrored in netlify/functions/lib/paywall-activity.ts — keep the two in sync.
type PaywallReason = 'out-of-credits' | 'no-subscription'

interface SubscriptionGateProps {
  /**
   * When provided, renders a close button so the gate can be dismissed. Used when the gate is
   * triggered by an action (e.g. clicking "New Exploration" with no credits) rather than by a
   * whole route being gated — in the route case the gate is left non-dismissible (navigate away
   * via the sidebar instead).
   */
  onClose?: () => void
}

/**
 * SubscriptionGate — full-screen plan picker shown when the logged-in user has run out of credits
 * and tries to start new work (see RequireCredits and the New Exploration button). Checkout (free
 * or paid) does a full-page redirect back to ?checkout=success, so the app reloads and the gate
 * re-evaluates; this just strips the leftover query param.
 */
export default function SubscriptionGate({ onClose }: SubscriptionGateProps = {}) {
  // The gate confronts two distinct situations, which need different framing: a user WITH an active
  // subscription who has simply used up their credits, versus a user with NO subscription yet (new
  // signup, or a lapsed/cancelled plan) who must subscribe before they can run anything.
  const { subscription, isLoading } = useSubscription()
  const hasSubscription = !isLoading && subscription != null

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const checkout = params.get('checkout')
    if (checkout === 'success' || checkout === 'cancelled') {
      const next = new URL(window.location.href)
      next.searchParams.delete('checkout')
      window.history.replaceState(null, '', next.toString())
    }
  }, [])

  // Record that the paywall confronted this user. Fires once per mount (the admin section de-dupes to
  // distinct users), but only AFTER the subscription fetch resolves: `reason` is derived from
  // hasSubscription, which is meaningless while isLoading is still true (we default it to "no
  // subscription" for the prompt copy above), so firing at mount would misclassify every impression
  // as 'no-subscription'. `dismissed` on close/logout is already tracked via the button
  // data-analytics-event tags below.
  const paywallEventFired = useRef(false)
  useEffect(() => {
    if (isLoading || paywallEventFired.current) return
    paywallEventFired.current = true
    const reason: PaywallReason = hasSubscription ? 'out-of-credits' : 'no-subscription'
    trackEvent(PAYWALL_SHOWN_EVENT, { reason })
    // Mirror to Segment/Intercom. `dismissible` distinguishes an action-triggered gate (e.g. "New
    // Exploration" with no credits) from a whole route being gated.
    trackSegmentEvent('Hit paywall', { dismissible: !!onClose, reason })
  }, [isLoading, hasSubscription, onClose])

  return (
    <div className="subscription-gate">
      <div className="subscription-gate__inner">
        {onClose && (
          <button
            type="button"
            className="subscription-gate__close"
            onClick={onClose}
            aria-label="Close"
            data-testid="subscription-gate-close"
            data-analytics-event="plan-gate-close-button"
          >
            <X size={20} aria-hidden />
          </button>
        )}
        {/* Which of the two headings is right depends on the subscription, so wait for it rather than
            guessing — otherwise a subscriber is told to "choose a plan to get started" and the heading
            then rewrites itself to "you're out of credits". PlanSelection waits on the same fetch, so
            nothing below could render yet either. The label matches the one RequireCredits shows on the
            way in, making the two waits read as one. */}
        {isLoading ? (
          <LoadingPane label="Checking your plan…" data-testid="subscription-gate-loading" />
        ) : (
          <>
            <div className="subscription-gate__head">
              <h1 className="subscription-gate__title">
                {hasSubscription ? "You're out of credits" : 'Choose a plan to get started'}
              </h1>
              <p className="subscription-gate__subtitle">
                {hasSubscription
                  ? 'Upgrade your plan to keep running explorations, journeys, and polish passes.'
                  : 'Sign up for a plan to start running explorations, journeys, and polish passes.'}
              </p>
            </div>
            {/* The picker loads as a deferred chunk (see the note at the top of the file), so this
                Suspense pair covers its fetch: the existing wait pane holds the layout while it
                streams in, and a persistently failed chunk (a stale-deploy 404) degrades to an
                inline reload prompt instead of the root error boundary blanking the whole page. */}
            <ChunkErrorBoundary
              eventTag="plan-selection"
              fallback={
                <div role="alert">
                  <p>Couldn’t load the plan picker. Reload to get the latest version.</p>
                  <button type="button" onClick={() => window.location.reload()}>Reload page</button>
                </div>
              }>
              <Suspense
                fallback={<LoadingPane label="Loading plans…" data-testid="subscription-gate-plans-loading" />}>
                <PlanSelection surface="gate" />
              </Suspense>
            </ChunkErrorBoundary>
          </>
        )}
        <div className="subscription-gate__footer">
          <button
            type="button"
            className="subscription-gate__logout"
            onClick={() => logout('/')}
            data-analytics-event="plan-gate-logout-button"
          >
            <LogOut size={16} aria-hidden /> Log out
          </button>
        </div>
      </div>
    </div>
  )
}
